import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { unSlugifyURL } from "@/app/handlers/urlFunctions.js";
import { useJob } from "@/app/services/create-resume/queries";

import { ApplicationDetails, getApplicationDetails, JobDetails } from "../utils/helpers";

interface UseJobDetailsReturn {
	jobId: string;
	jobDetails: JobDetails;
	setJobDetails: React.Dispatch<React.SetStateAction<JobDetails>>;
	loading: boolean;
	applicationDetails: ApplicationDetails;
	setApplicationDetails: React.Dispatch<React.SetStateAction<ApplicationDetails>>;
}

/**
 * Hook to manage job details fetching and state
 * Uses React Query for caching - prefetched data will be used if available
 * @param data - Optional job data passed as prop
 * @returns Job details state and helpers
 */
export function useJobDetails(data?: { _id?: string }): UseJobDetailsReturn {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();

	const jobId = useMemo(() => {
		return unSlugifyURL(id) as string;
	}, [id]);

	const effectiveJobId = data?._id || jobId;

	const { data: jobResponse, isLoading, isError } = useJob(effectiveJobId);

	const [jobDetails, setJobDetails] = useState<JobDetails>({});
	const [applicationDetails, setApplicationDetails] = useState<ApplicationDetails>({
		link: null,
		jobId: null,
		companyId: null,
		applicationType: "URL",
	});

	useEffect(() => {
		if (jobResponse?.data) {
			setJobDetails(jobResponse.data);
			setApplicationDetails(getApplicationDetails(jobResponse.data));
		}
	}, [jobResponse]);

	useEffect(() => {
		if (isError) {
			navigate("/jobs");
		}
	}, [isError, navigate]);

	// Use query data directly when available, fall back to state
	// Loading is true until we have actual job data
	const effectiveJobDetails = jobResponse?.data || jobDetails;
	const loading = isLoading || (!jobResponse?.data && !isError);

	return {
		jobId: effectiveJobId,
		jobDetails: effectiveJobDetails,
		setJobDetails,
		loading,
		applicationDetails,
		setApplicationDetails,
	};
}
